import { Suspense, use, type ComponentProps } from "react";
import { Link } from "@tanstack/react-router";
import { JsonLd } from "@/components/seo/json-ld";
import { blog } from "../lib/collections";
import { CollectionCards } from "./collection-cards";
import type { ContentPageData } from "../lib/server";
import { getMDXComponents } from "./mdx";
import { relForUrl, blogHref } from "../lib/blog-links";
import { ArticlePage } from "./article/article-page";

function BlogLink({ href, children, ...props }: ComponentProps<"a">) {
  // Pending marketing pages are retained as text until their migration is ready.
  const target = href ? blogHref(href) : undefined;
  if (!target) return <span>{children}</span>;
  return (
    <a {...props} href={target} rel={relForUrl(target)}>
      {children}
    </a>
  );
}

function Body({ data }: { data: ContentPageData }) {
  const page = blog.getPage(data.path);
  if (!page) throw new Error(`Unknown blog page: ${data.path}`);
  use(page.load());
  const MDX = page.body;
  const post = data.blog;
  return (
    <main id="marketing-main" className="mx-auto max-w-7xl px-6 md:px-10">
      <JsonLd data={data.schemas.flat()} />
      <header className="border-b border-hb-card py-16 md:py-24">
        <p className="font-mono-hb mb-6 text-xs uppercase tracking-widest text-hb-accent">
          {post ? (
            <Link to="/blog" preload="intent">
              Blog / All posts
            </Link>
          ) : (
            "Blog"
          )}
        </p>
        <h1 className="max-w-5xl text-4xl font-extrabold leading-tight tracking-tight md:text-6xl">
          {data.title}
        </h1>
        <p className="mt-6 max-w-3xl text-lg leading-relaxed text-hb-muted md:text-xl">
          {data.description}
        </p>
        {post?.datePublished && (
          <div className="font-mono-hb mt-8 flex flex-wrap gap-4 text-sm text-hb-muted">
            <time dateTime={post.datePublished}>{post.datePublished}</time>
            <span>
              {post.dateModified !== post.datePublished
                ? `Updated ${post.dateModified}`
                : "Originally published"}
            </span>
          </div>
        )}
      </header>
      <article
        aria-label={data.title}
        className={`marketing-prose prose min-w-0 py-12 ${post ? "blog-article mx-auto max-w-[80ch]" : "max-w-none"}`}
      >
        {post?.heading && post.heading !== post.title && <h2>{post.title}</h2>}
        <MDX
          components={getMDXComponents({
            BlogPostCards: () => <CollectionCards cards={data.cards} />,
            a: BlogLink,
          })}
        />
      </article>
      {post && (
        <Link
          to="/blog"
          preload="intent"
          className="inline-flex border-t border-hb-card py-6 text-sm font-bold text-hb-accent"
        >
          ← All posts
        </Link>
      )}
    </main>
  );
}

export function BlogPage({ data }: { data: ContentPageData }) {
  if (data.article) return <ArticlePage data={data} />;
  return (
    <Suspense fallback={<div className="p-8 text-hb-muted">Loading article...</div>}>
      <Body data={data} />
    </Suspense>
  );
}
